"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ObservableMutationView = void 0;
/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
var mergeAll_1 = __importDefault(require("lodash/fp/mergeAll"));
var EqualityComparers_1 = require("../EqualityComparers");
var Observable_1 = require("../observables/Observable");
var Utilities_1 = require("../Utilities");
/**
 * `ObservableMutationView<T>` is an `IReadonlyObservable` that allows you to compose an object out of 'mutations'
 * that are added in-order, but where each can be removed individually at a later time. This is particurally useful for
 * optimistic updates to a data model object that 1. do not mutate the original object and 2. allow multiple unrelated
 * mutations of that object that can be reverted individually.
 *
 * Each call to `mutate` returns an IDisposable 'handle' that, when disposed, removes that mutation from the stack.
 *
 * Example from `ObservableMutationView.test.ts`:
 * Each mutation object is merged onto the stack. The `value` of the view is always an object that is the product of a
 * deep merge of all of the mutations on the stack.
 *
 * By calling `dispose()` on one of the mutations lower in the stack, its changes are removed and the view is
 * rematerialized
 *
 * ```typescript
const view = new ObservableMutationView<ITest>();
view.mutate({ a: 1 });
view.mutate({ b: 'hello' });
view.mutate({ c: { d: 1 } });
const toRemove = view.mutate({ c: { d: 2 } });
view.mutate({ b: 'bye' });

toRemove.dispose();
```
 * The `value` is now a deep merge of each object, but no longer including `{ c: { d: 2 } }`.
 */
var ObservableMutationView = /** @class */ (function () {
    function ObservableMutationView() {
        this._currentValue = new Observable_1.Observable({}, EqualityComparers_1.deepEqualityComparer);
        this._mutationStack = [{}];
    }
    ObservableMutationView.prototype._update = function () {
        this._currentValue.value = (0, mergeAll_1.default)(this._mutationStack);
    };
    Object.defineProperty(ObservableMutationView.prototype, "value", {
        get: function () {
            return this._currentValue.value;
        },
        enumerable: false,
        configurable: true
    });
    ObservableMutationView.prototype.subscribe = function (handler) {
        return this._currentValue.subscribe(handler);
    };
    ObservableMutationView.prototype.mutate = function (value) {
        var _this = this;
        this._mutationStack.push(value);
        this._update();
        return Object.freeze({
            dispose: function () {
                (0, Utilities_1.removeFromArray)(value, _this._mutationStack);
                _this._update();
            },
        });
    };
    ObservableMutationView.prototype.clear = function () {
        this._mutationStack = [{}];
        this._update();
    };
    ObservableMutationView.prototype.dispose = function () {
        this._currentValue.dispose();
    };
    return ObservableMutationView;
}());
exports.ObservableMutationView = ObservableMutationView;
//# sourceMappingURL=ObservableMutationView.js.map